import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Checkbox,
  TableContainer,
  Paper,
  ListItemText,
  MenuItem,
} from '@mui/material';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { formatSuccessMessage, formatErrorMessage, convertToLocalDate } from '../../utils/common';
import { showAlert as showGlobalAlert, clearAllAlerts as clearAllGlobalAlerts } from '../../store/actions/alertActions';
import { hide, show } from '../../store/actions/busyIndicatorActions';
import EnumService from '../../services/EnumService';
import { EnumRes, FleetIoFormType } from '../../proxy/proxy';
import FleetIoService from '../../services/FleetIoService';
import Table from '../Table';

interface Props {
  open: boolean;
  stateId: number;
  handleModalClose: (data: any, formType: number) => any;
  showAlert: (message: any) => any;
  clearAllAlerts: () => any;
  showBusyIndicator: () => any;
  hideBusyIndicator: () => any;
  selectedForms?: Array<{ id: number; title: string; type: number }>;
}

interface FormValues {
  formType: number;
}

interface FleetIoForm {
  id: number;
  title: string;
  description: string;
  created_at: string;
  [key: string]: any;
}

const validationSchema = Yup.object().shape({
  formType: Yup.number().required('Form Type is required'),
});

const FetchFleetIoForms: React.FC<Props> = ({
  open,
  stateId,
  handleModalClose,
  showAlert,
  clearAllAlerts,
  showBusyIndicator,
  hideBusyIndicator,
  selectedForms = [],
}) => {
  const [fleetIoFormTypes, setFleetIoFormTypes] = useState<Array<EnumRes>>([]);
  const [formsData, setFormsData] = useState<FleetIoForm[]>([]);
  const [selectedFormsState, setSelectedFormsState] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [selectedFormType, setSelectedFormType] = useState<number>(FleetIoFormType._0);
  const [sortModel, setSortModel] = useState<any[]>([]);

  useEffect(() => {
    EnumService.getEnums('fleetIoFormTypes').then((formTypes) => {
      setFleetIoFormTypes(formTypes);
    }).catch();
  }, []);

  useEffect(() => {
    if (!open) {
      setFormsData([]);
      setSelectedFormsState([]);
    } else if (selectedForms && selectedForms.length > 0) {
      // Pre-select forms matching the selected type when modal opens or type changes
      setSelectedFormsState(selectedForms.filter((f) => f.type === selectedFormType).map((f) => f.id));
    } else {
      setSelectedFormsState([]);
    }
  }, [open, selectedForms, selectedFormType]);

  const fetchForms = (values: FormValues) => {
    showBusyIndicator();
    clearAllAlerts();

    setSelectedFormType(values.formType);

    const payload = {
      actionId: 901,
      stateId
    };

    FleetIoService.fleetIoPage(payload)
      .then((response) => {
        if (response?.records?.length > 0) {
          showAlert(formatSuccessMessage('Forms successfully fetched'));
          setFormsData(response.records);
        } else {
          showAlert(formatErrorMessage({ title: 'No forms available' }));
          setFormsData([]);
        }
      })
      .catch((err) => {
        showAlert(formatErrorMessage(JSON.parse(err.response)));
      })
      .finally(() => {
        hideBusyIndicator();
      });
  };

  const handleClose = () => {
    handleModalClose(null, selectedFormType);
  };
  const handleImport = () => {
    const selectedData = formsData.filter((form) => selectedFormsState.includes(form.id));
    // Also include pre-selected forms that may not be in formsData (if needed)
    // But since formsData is the fetched list, and selectedFormsState is the union, this covers all
    handleModalClose(selectedData, selectedFormType);
  };

  const handleRowSelectionChange = (ids: any[]) => {
    setSelectedFormsState(ids);
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      flex: 1
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 250,
      renderCell: (params: any) => new Date(params.value).toLocaleString()
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>Fetch FleetIO Forms</DialogTitle>
      <Formik
        initialValues={{
          formType: FleetIoFormType._0
        }}
        validationSchema={validationSchema}
        onSubmit={fetchForms}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              { formsData.length === 0 && (
                <>
                  <div style={{ marginBottom: '20px', display: 'flex', gap: '16px' }}>
                    <TextField
                      style={{ flex: 1 }}
                      fullWidth
                      select
                      label="Form Type"
                      name="formType"
                      value={values.formType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(touched.formType && errors.formType)}
                      helperText={touched.formType && errors.formType}
                      margin="normal"
                    >
                      {fleetIoFormTypes.map((formType) => (
                        <MenuItem key={formType.id} value={formType.id}>
                          <ListItemText primary={formType.value} />
                        </MenuItem>
                      ))}
                    </TextField>
                  </div>
                  <DialogActions>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Fetch Forms
                    </Button>
                  </DialogActions>
                </>
              )}

              {formsData.length > 0 && (
                <>
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table
                      rows={formsData}
                      columns={columns}
                      page={currentPage}
                      totalRows={formsData.length}
                      sortModel={sortModel}
                      onPageChange={setCurrentPage}
                      onSortModelChange={setSortModel}
                      checkboxSelection
                      onSelectionModelChange={handleRowSelectionChange}
                      selectionModel={selectedFormsState}
                    />
                  </TableContainer>

                  <DialogActions>
                    <Button onClick={handleClose} color="primary">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleImport}
                      color="primary"
                      variant="contained"
                      disabled={selectedFormsState.length === 0}
                    >
                      Import Selected
                    </Button>
                  </DialogActions>
                </>
              )}
            </DialogContent>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const mapDispatchToProps = {
  showAlert: showGlobalAlert,
  clearAllAlerts: clearAllGlobalAlerts,
  showBusyIndicator: show,
  hideBusyIndicator: hide,
};

export default connect(null, mapDispatchToProps)(FetchFleetIoForms);
